import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, MessageSquare, ArrowLeft, Loader2, MapPin, GraduationCap, Phone, Send, LayoutDashboard, LogOut, Home } from 'lucide-react';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const JobDetails: React.FC = () => {
  const { t } = useTranslation();
  const { jobId } = useParams<{ jobId: string }>();
  const [job, setJob] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await api.get(`/jobs/${jobId}`);
        setJob(res.data);

        if (res.data.status === 'ACCEPTED' || res.data.status === 'COMPLETED') {
          setHasApplied(true);
        }
      } catch (err) {
        console.error("Error fetching job:", err);
      }
    };
    if (jobId) fetchJob();
  }, [jobId]);

  const handleApply = async () => {
    if (loading || hasApplied) return;
    setLoading(true);
    try {
      await api.post('/jobs/apply', {
        jobId: jobId,
        message: "I am interested in this tutoring position and have the required expertise."
      });
      setHasApplied(true);
      alert(t('application_sent', "🚀 Application Sent! The Admin will review your request."));
    } catch (err: any) {
      if (err.response?.status === 400) {
        setHasApplied(true);
        alert(t('already_applied', "ℹ️ You have already applied for this job."));
      } else {
        alert(t('apply_error', "❌ Error: Could not submit application."));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!job) return (
    <div className="h-screen w-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex flex-col items-center justify-center text-white italic gap-4">
      <Loader2 className="animate-spin text-indigo-500" size={40} />
      <span className="text-xs font-black uppercase tracking-widest">{t('loading_details', 'Loading Details...')}</span>
    </div>
  );

  return (
    <div className="h-full w-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex flex-col overflow-hidden relative">

      <div className="h-14 shrink-0 bg-slate-900/50 backdrop-blur-md border-b border-slate-800 flex items-center justify-between px-4 z-50">
        <button
          onClick={() => navigate('/tutor/home')} 
          className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-400 hover:text-indigo-400 transition-all"
        >
          <LayoutDashboard size={14} />
          <span>{t('dashboard', 'Dashboard')}</span>
        </button>

        <button
          onClick={() => window.confirm(t('confirm_logout', "Logout?")) && logout()}
          className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-500 hover:text-red-500 transition-all"
        >
          <span>{t('logout', 'Logout')}</span>
          <LogOut size={14} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar scroll-smooth">
        <div className="w-full max-w-2xl mx-auto p-3 md:p-8 lg:p-12 pb-32">

          <div className="bg-slate-900/80 backdrop-blur-md border border-slate-800 rounded-[2rem] md:rounded-[3rem] overflow-hidden shadow-2xl flex flex-col">
            <div className="bg-gradient-to-br from-indigo-600 to-violet-700 p-5 md:p-10 text-center relative shrink-0">
              <button
                onClick={() => navigate(-1)}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all active:scale-90"
              >
                <ArrowLeft size={18} />
              </button>
              <h2 className="text-xl md:text-3xl font-black italic uppercase tracking-tighter leading-none text-white">{t('job_details', 'Job Details')}</h2>
              <p className="text-indigo-100 font-bold mt-2 text-[9px] md:text-xs tracking-widest uppercase opacity-70">
                {job.subject}
              </p>
            </div>
          </div>

          <div className="p-4 md:p-10 space-y-4 md:space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 bg-slate-950/50 p-4 rounded-2xl border border-slate-800/50">
                <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-indigo-400 shrink-0">
                  <User size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-[8px] font-black uppercase text-slate-600 tracking-widest leading-none mb-1">{t('student', 'Student')}</p>
                  <h3 className="text-sm md:text-base font-bold truncate text-slate-200">{job.student?.fullName || t('student', "Student")}</h3>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-slate-950/50 p-4 rounded-2xl border border-slate-800/50">
                <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-green-400 shrink-0">
                  <DollarSign size={18} />
                </div>
                <div>
                  <p className="text-[8px] font-black uppercase text-slate-600 tracking-widest leading-none mb-1">{t('budget', 'Budget')}</p>
                  <h3 className="text-base md:text-xl font-black text-green-400">{job.budget} <span className="text-[10px]">ETB</span></h3>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-slate-800/30 px-3 py-1.5 rounded-full text-slate-400 border border-slate-700/50 text-[9px] md:text-xs">
                <MapPin size={12} className="text-indigo-500" />
                <span className="font-black uppercase tracking-tight">{job.location}</span>
              </div>
              <div className="flex items-center gap-2 bg-slate-800/30 px-3 py-1.5 rounded-full text-slate-400 border border-slate-700/50 text-[9px] md:text-xs">
                <GraduationCap size={12} className="text-indigo-500" />
                <span className="font-black uppercase tracking-tight">{job.gradeLevel}</span>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="flex items-center gap-2 text-indigo-400 font-black uppercase text-[8px] md:text-[10px] tracking-[0.2em] px-1">
                <MessageSquare size={12} /> {t('description', 'Description')}
              </h4>
              <div className="text-slate-300 text-xs md:text-base leading-relaxed font-medium bg-slate-950/50 p-4 md:p-7 rounded-2xl border border-slate-800/50 min-h-[80px]">
                {job.description}
              </div>
            </div>

            <div className="pt-2">
              {job.status === 'ACCEPTED' ? (
                <div className="bg-green-500/5 border border-green-500/20 p-5 rounded-2xl text-center">
                  <p className="text-[8px] font-black uppercase text-green-500 tracking-widest mb-2">{t('details_unlocked', 'Details Unlocked')}</p>
                  <div className="flex items-center justify-center gap-3 text-xl md:text-2xl font-black text-white">
                    <Phone className="text-green-500" size={20} />
                    <span>{job.parentPhone}</span>
                  </div>
                </div>
              ) : hasApplied ? (
                <div className="bg-amber-500/5 border border-amber-500/20 p-5 rounded-2xl text-center">
                  <div className="flex items-center justify-center gap-2 text-amber-500 font-black uppercase tracking-widest text-[10px]">
                    <Loader2 className="animate-spin" size={14} /> {t('pending_approval', 'Pending Approval')}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => navigate(-1)}
                    className="w-full py-3.5 bg-slate-800 hover:bg-slate-700 rounded-xl font-black uppercase tracking-widest text-[10px] border border-slate-700 transition-all active:scale-95"
                  >
                    {t('back', 'Back')}
                  </button>
                  <button
                    onClick={handleApply}
                    disabled={loading}
                    className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-500 rounded-xl font-black uppercase tracking-widest shadow-xl shadow-indigo-900/40 flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 text-[10px] text-white"
                  >
                    {loading ? <Loader2 className="animate-spin" size={14} /> : <Send size={16} />}
                    {t('apply_now', 'Apply Now')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <button onClick={() => navigate('/tutor/home')} className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-500 text-white p-4 rounded-full shadow-2xl shadow-blue-500/30 transition-all hover:-translate-y-1 z-50 group flex items-center gap-0 overflow-hidden">
        <Home size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-3 transition-all duration-300 ease-in-out whitespace-nowrap font-bold text-sm uppercase tracking-wider">{t('dashboard', 'Dashboard')}</span>
      </button>
    </div>
  );
};

export default JobDetails;

const DollarSign = ({ size, className }: { size: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);